import type { FetcherKind } from './errors';
import type { KeyValueStore } from '../loglist/fetch';

/** Which API surface a request belonged to; logs often allow some and block others. */
export type EndpointClass = 'tree-head' | 'entries' | 'proof' | 'roots' | 'submit' | 'tile' | 'issuer';

export type ReachStatus = 'ok' | 'cors-blocked' | 'error';
export interface ReachabilityRecord {
  status: ReachStatus;
  via?: FetcherKind;
  at: number;
  error?: string;
}

export type ReachSummary = 'direct' | 'proxy' | 'partial' | 'blocked' | 'unknown';

const KEY = 'ct:reach:v1';

/** Remembers, per (log, endpoint class), whether the browser could reach it and how. */
export class ReachabilityStore {
  private map = new Map<string, ReachabilityRecord>();
  private listeners = new Set<() => void>();

  constructor(private readonly storage?: KeyValueStore) {
    try {
      const raw = storage?.getItem(KEY);
      if (raw) {
        const obj = JSON.parse(raw) as Record<string, ReachabilityRecord>;
        for (const [k, v] of Object.entries(obj)) this.map.set(k, v);
      }
    } catch {
      /* ignore corrupt storage */
    }
  }

  private static k(logId: string, cls: EndpointClass): string {
    return `${logId}|${cls}`;
  }

  get(logId: string, cls: EndpointClass): ReachabilityRecord | undefined {
    return this.map.get(ReachabilityStore.k(logId, cls));
  }

  set(logId: string, cls: EndpointClass, rec: ReachabilityRecord): void {
    this.map.set(ReachabilityStore.k(logId, cls), rec);
    this.persist();
    for (const l of this.listeners) l();
  }

  clear(): void {
    this.map.clear();
    this.persist();
    for (const l of this.listeners) l();
  }

  records(logId: string): Partial<Record<EndpointClass, ReachabilityRecord>> {
    const out: Partial<Record<EndpointClass, ReachabilityRecord>> = {};
    for (const [k, v] of this.map) {
      const [id, cls] = k.split('|') as [string, EndpointClass];
      if (id === logId) out[cls] = v;
    }
    return out;
  }

  /** Coarse badge state for a log. */
  summary(logId: string): ReachSummary {
    const recs = Object.values(this.records(logId));
    if (recs.length === 0) return 'unknown';
    const okDirect = recs.some((r) => r.status === 'ok' && r.via === 'direct');
    const okOther = recs.some((r) => r.status === 'ok' && r.via !== 'direct');
    const blocked = recs.some((r) => r.status === 'cors-blocked');
    if (blocked && !okDirect && !okOther) return 'blocked';
    if (blocked) return 'partial';
    if (okOther && !okDirect) return 'proxy';
    if (okDirect || okOther) return 'direct';
    return 'unknown';
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private persist(): void {
    try {
      this.storage?.setItem(KEY, JSON.stringify(Object.fromEntries(this.map)));
    } catch {
      /* storage unavailable */
    }
  }
}
